import React, { useMemo } from 'react';
import { scoreDetailsOrder, morphologyDetailsOrder } from './constants';

interface AutoScoresProps {
  scoreDetails: { [key: string]: string | number };
  morphologyDetails: { [key: string]: string | number };
  ultravioletDetails?: { [key: string]: string | number };
}

const getUVScoreData = (ultravioletDetails: { [key: string]: string | number }) => {
  const entries = Object.entries(ultravioletDetails);
  if (entries.length === 0) {
    return null;
  }

  const highest = entries.reduce((prev, curr) => {
    const prevVal = parseFloat(String(prev[1]));
    const currVal = parseFloat(String(curr[1]));
    return currVal > prevVal ? curr : prev;
  });

  const [highestKey] = highest;
  const isNegative = highestKey.toLowerCase() === 'negative';

  return {
    textColor: isNegative ? 'text-red-600' : 'text-green-600',
    displayText: isNegative ? 'Negative' : 'Positive',
  };
};

const AutoScores = ({
  scoreDetails = {},
  morphologyDetails = {},
  ultravioletDetails = {},
}: AutoScoresProps) => {
  const orderedScoreDetails = useMemo(() => {
    return scoreDetailsOrder
      .filter((key) => Object.prototype.hasOwnProperty.call(scoreDetails, key))
      .map((key) => [key, scoreDetails[key]]);
  }, [scoreDetails]);

  const orderedMorphologyDetails = useMemo(() => {
    return morphologyDetailsOrder
      .filter((key) => Object.prototype.hasOwnProperty.call(morphologyDetails, key))
      .map((key) => [key, morphologyDetails[key]]);
  }, [morphologyDetails]);

  const uvScoreData = useMemo(
    () => getUVScoreData(ultravioletDetails),
    [ultravioletDetails],
  );

  const isScoreDetailsEmpty = Object.keys(scoreDetails).length === 0;
  const isMorphologyDetailsEmpty = Object.keys(morphologyDetails).length === 0;
  const isUltravioletDetailsEmpty = Object.keys(ultravioletDetails).length === 0;

  if (isScoreDetailsEmpty && isMorphologyDetailsEmpty && isUltravioletDetailsEmpty) {
    return null;
  }

  return (
    <div
      id="commandpanel-exp-autoscore-wrapper"
      className="flex flex-row mt-4 ml-4 overflow-hidden"
    >
      {!isScoreDetailsEmpty && (
        <div id="commandpanel-exp-autoscore-container" className="flex-1">
          <h2
            id="commandpanel-exp-autoscore-header"
            className="font-medium text-lg text-gray-700 mb-4"
          >
            Outcome
          </h2>
          <div id="commandpanel-exp-autoscore-columns" className="flex flex-col gap-2">
            {orderedScoreDetails.map(([key, value], index) => (
              <div
                id={`commandpanel-exp-autoscore-row-${index}`}
                key={index}
                className="flex items-center mb-2"
              >
                <div
                  id={`commandpanel-exp-autoscore-label-${index}`}
                  className="font-medium text-sm leading-[0.8rem] text-gray-700 w-32 mr-3"
                >
                  {String(key).toLowerCase()}
                </div>
                <div
                  id={`commandpanel-exp-autoscore-value-${index}`}
                  className="text-sm leading-[0.8rem] text-green-600 w-32"
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isScoreDetailsEmpty &&
        (!isMorphologyDetailsEmpty || !isUltravioletDetailsEmpty) && (
          <div
            id="commandpanel-exp-autoscore-divider-right"
            className="divider-right"
          ></div>
        )}

      {(!isMorphologyDetailsEmpty || !isUltravioletDetailsEmpty) && (
        <div id="commandpanel-exp-morphology-container" className="flex-1 pl-[3rem]">
          {!isMorphologyDetailsEmpty && (
            <>
              <h2
                id="commandpanel-exp-morphology-header"
                className="font-medium text-lg text-gray-700 mb-4"
              >
                Morphology Score
              </h2>
              <div
                id="commandpanel-exp-morphology-columns"
                className="flex flex-col gap-2"
              >
                {orderedMorphologyDetails.map(([key, value], index) => (
                  <div
                    id={`commandpanel-exp-morphology-row-${index}`}
                    key={index}
                    className="flex items-center mb-2"
                  >
                    <div
                      id={`commandpanel-exp-morphology-label-${index}`}
                      className="font-medium text-sm leading-[0.8rem] text-gray-700 w-32 mr-3"
                    >
                      {String(key).toLowerCase()}
                    </div>
                    <div
                      id={`commandpanel-exp-morphology-value-${index}`}
                      className="text-sm leading-[0.8rem] text-green-600 w-32"
                    >
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {uvScoreData && (
            <>
              <h2 className="font-medium text-lg text-gray-700 mb-4 mt-6">UV Score</h2>
              <div className="flex items-center mb-5">
                <div className="font-medium text-sm leading-[0.8rem] text-gray-700 w-32 mr-3">
                  Hit
                </div>
                <div
                  className={`text-sm leading-[0.8rem] ${uvScoreData.textColor} font-semibold`}
                >
                  {uvScoreData.displayText}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AutoScores;
